<template>
  <NTable
    :data="symbols"
    :sort-by="{
      prop: 'instrumentTitle',
      order: 'descending',
    }"
    cell-padding="small"
    :column-splitter="false"
    :striped="false"
    class="mb-2"
    hover-alignment="right"
    hover-offset="15%"
    virtual-scroll
    fixed-header
    @click.stop
    @row-click="(symbol) => updateActiveIsin(symbol.instrumentIsin)"
  >
    <template #hoveredElements="{ row }">
      <template v-if="row.hasSellRequest">
        <NegarEditIcon
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'embeddedPutOptionTable',
            component: 'editIcon',
          }"
          size="20"
          class="mx-2 cursor-pointer"
          @click.stop="handleOpenForm(row, true)"
        />

        <NegarTrashLightIcon
          ref="deleteEmbeddedPutOptionButtonRef"
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'embeddedPutOptionTable',
            component: 'trashIcon',
          }"
          size="20"
          class="text-critical-primary ml-2 cursor-pointer"
          @click.stop="handleOpenDeleteDialog(row)"
        />

        <NegarInfoWithBorderIcon
          v-gid="{
            module: 'portfolio',
            screen: 'home',
            section: 'embeddedPutOptionTable',
            component: 'infoIcon',
          }"
          size="20"
          class="cursor-pointer"
          @click.stop="handleOpenInfoDialog(row)"
        />
      </template>

      <NButton
        v-else
        v-gid="{
          module: 'portfolio',
          screen: 'home',
          section: 'embeddedPutOptionTable',
          component: 'applyButton',
        }"
        variant="fill"
        size="mini"
        class="font-medium w-16"
        @click.stop="handleOpenForm(row, false)"
      >
        {{ t('misc.apply') }}
      </NButton>
    </template>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.symbol')"
      prop="instrumentTitle"
      sortable
      align="text-right"
    >
      <NText :key="row.instrumentTitle" variant="caption2" weight="medium">
        {{ row.instrumentTitle }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.quantity')"
      prop="quantity"
      sortable
      align="text-right"
    >
      <NumberFormatter #="{ number }" :value="row.quantity">
        <ChangePriceEffect :key="row.quantity" :price="row.quantity">
          <NText variant="caption2" weight="medium">
            {{ number }}
          </NText>
        </ChangePriceEffect>
      </NumberFormatter>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.embeddedPutOption.dueDate')"
      prop="dueDate"
      sortable
      align="text-right"
    >
      <NText :key="row.dueDate" variant="caption2" weight="medium">
        {{ dateFormatter(row.dueDate, false) }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.embeddedPutOption.startDate')"
      prop="startDateOfAcceptanceOfTheRequest"
      sortable
      align="text-right"
    >
      <NText
        :key="row.startDateOfAcceptanceOfTheRequest"
        variant="caption2"
        weight="medium"
      >
        {{ dateFormatter(row.startDateOfAcceptanceOfTheRequest) }}
      </NText>
    </NTableColumn>

    <NTableColumn
      v-slot="{ row }"
      :label="t('modules.portfolio.embeddedPutOption.endDate')"
      prop="endDateOfAcceptanceOfTheRequest"
      sortable
      align="text-right"
    >
      <NText
        :key="row.endDateOfAcceptanceOfTheRequest"
        variant="caption2"
        weight="medium"
      >
        {{ dateFormatter(row.endDateOfAcceptanceOfTheRequest) }}
      </NText>
    </NTableColumn>
  </NTable>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDialog } from '@negar/core';
import {
  NegarEditIcon,
  NegarInfoWithBorderIcon,
  NegarTrashLightIcon,
} from '@negar/icons';
import {
  EmbeddedPutOptionSymbol,
  EmbeddedPutOptionSymbolsQuery,
} from '@libs/api-reference';

import {
  EmbeddedPutOptionDeleteDialog,
  EmbeddedPutOptionForm,
  EmbeddedPutOptionInfoDialog,
} from '@/modules/portfolio';
import { useActiveSymbolIsin } from '@/modules/symbol';

import ChangePriceEffect from '@/components/ChangePriceEffect.vue';
import NumberFormatter from '@/components/Number.vue';

type EmbeddedPutOptionTablePropsType = {
  symbols: EmbeddedPutOptionSymbolsQuery;
};

defineProps<EmbeddedPutOptionTablePropsType>();

const deleteEmbeddedPutOptionButtonRef = ref<HTMLElement>();

const { t, d } = useI18n();

const { add: addDialog } = useDialog();

const { updateActiveIsin } = useActiveSymbolIsin();

const dateFormatter = (date, showTime = true) =>
  `${d(date, 'shortDate')}
    ${showTime ? ` ,${d(date, 'shortTime')}` : ''}`;

const handleOpenForm = (symbol: EmbeddedPutOptionSymbol, isEdit) => {
  addDialog({
    title: symbol.instrumentTitle || '',
    bodyClass: '!w-[375px]',
    content: computed(() => ({
      component: EmbeddedPutOptionForm,
      props: {
        symbol: symbol,
        isEdit,
      },
    })),
  });
};

const handleOpenInfoDialog = (symbol: EmbeddedPutOptionSymbol) => {
  addDialog({
    title: t('modules.portfolio.embeddedPutOption.title'),
    bodyClass: '!w-1/4',
    content: computed(() => ({
      component: EmbeddedPutOptionInfoDialog,
      props: {
        instrumentIsin: symbol.instrumentIsin || '',
      },
    })),
  });
};

const handleOpenDeleteDialog = (symbol: EmbeddedPutOptionSymbol) => {
  addDialog({
    title: t('modules.portfolio.embeddedPutOption.deleteTitle', {
      instrumentName: symbol.instrumentTitle || '',
    }),
    referenceElementRef: deleteEmbeddedPutOptionButtonRef,
    bodyClass: '!w-1/5',
    placement: 'top',
    content: computed(() => ({
      component: EmbeddedPutOptionDeleteDialog,
      props: {
        instrumentIsin: symbol.instrumentIsin || '',
        instrumentName: symbol.instrumentTitle || '',
      },
    })),
  });
};
</script>
